import {defineStore} from 'pinia'
import Srv, {GraphQLSrv} from '@/services/GraphQLSrv'
import {accessTokenStorage} from '@/helpers/storage'
import type {AuthReq} from '@/types/RequestAttrs'
import {devPemissions} from '@/helpers/roles'
import type {User} from '@/types/User'
import router from '@/router'

const guestSrv = new GraphQLSrv(import.meta.env.VITE_API_URL_GRAPHQL_GUEST)

const defaultUser = (): User => ({
  uuid: '',
  login: '',
  roleUuids: [],
  permissions: [],
  roles: [],
})

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: defaultUser(),
    accessToken: accessTokenStorage.get(),
    permissionsLoaded: false,
  }),
  getters: {
    userPermissions: (state) =>
      new Set(state.user.permissions.map((p) => p.name)),
    isAuth: (state) => !!state.accessToken,
    // `isAuth` означает лишь «есть токен» (нужно для Authorization-заголовка).
    // `isReady` — токен ЕСТЬ и init() завершён: стор-данные (views/roles/permissions)
    // гидрированы. UI-гейты, зависящие от прогруженного каталога/прав, должны
    // ориентироваться на `isReady`, иначе рендер уйдёт на пустых сторах после логина.
    isReady: (state) => !!state.accessToken && state.permissionsLoaded,
  },
  actions: {
    async authUser(body: AuthReq) {
      const {data, type, message} = await guestSrv.request(
        'mutation',
        'createAuthTokenMutation',
        ['accessToken', 'expiresAt'],
        false,
        body,
      )

      if (data?.accessToken) {
        accessTokenStorage.set(data.accessToken)
        this.accessToken = data.accessToken
      }

      return {type, message}
    },
    // Загрузка permissions по данным MeQuery (переиспользуется в queryMe и hydrateMeAndPermissions)
    // Батчит N запросов rolePermissions + 1 PermissionsQuery в 1 HTTP запрос
    async _loadPermissions(userData: any) {
      try {
        const userRoleUuids = userData.roleUuids || []

        if (userRoleUuids.length === 0) {
          userData.permissions = []
        } else {
          // Один батч-запрос: N rolePermissions (с алиасами) + PermissionsQuery
          const roleAliases = userRoleUuids.map((_: string, i: number) => `rp_${i}`)

          const batchQueries = [
            ...userRoleUuids.map((roleUuid: string, i: number) => ({
              name: 'rolePermissions',
              alias: roleAliases[i],
              fields: ['uuid', 'name', 'bindings', 'attributes'],
              includePagination: true,
              params: {
                page: 1,
                perPage: 10000,
                filters: {
                  role_uuid: {
                    operator: '=',
                    value: roleUuid,
                  },
                },
              },
            })),
            {
              name: 'PermissionsQuery',
              fields: ['uuid', 'name', 'parentUuid'],
              includePagination: true,
              params: {page: 1, perPage: 10000},
            },
          ]

          let batchData: any
          try {
            batchData = await Srv.batchQuery(batchQueries)
          } catch (batchErr: any) {
            // Partial-success: бэк отверг часть запросов (например, PermissionsQuery),
            // но rolePermissions для нашей роли может вернуться. Используем data,
            // иначе теряем все permissions при единичной ошибке.
            if (batchErr?.data) {
              console.warn('[auth] _loadPermissions: partial batch response', batchErr.errors)
              batchData = batchErr.data
            } else {
              throw batchErr
            }
          }

          // Объединяем результаты всех rolePermissions запросов
          const allRolePermissions: any[] = []
          roleAliases.forEach((alias: string) => {
            const rpData = batchData[alias]?.data || []
            allRolePermissions.push(...rpData)
          })

          const allPermissionsList = batchData.PermissionsQuery?.data || []

          // Извлечь UUID разрешений и states
          const permissionUuids = new Set<string>()
          const permissionStatesMap = new Map<string, string[]>()

          allRolePermissions.forEach((rp: any) => {
            const attrs: any = {}
            if (rp.attributes && Array.isArray(rp.attributes)) {
              rp.attributes.forEach((attr: any) => {
                const key = attr.name || attr.key
                if (key) {
                  let value = attr.value
                  if (
                    key !== 'states' &&
                    Array.isArray(value) &&
                    value.length === 1
                  ) {
                    value = value[0]
                  }
                  attrs[key] = value
                }
              })
            }

            const permUuids = attrs.permission_uuid || []
            const states = attrs.states || []

            const permUuidsArray = Array.isArray(permUuids)
              ? permUuids
              : [permUuids]
            const statesArray = Array.isArray(states) ? states : [states]

            permUuidsArray.forEach((uuid: string) => {
              permissionUuids.add(uuid)
              const existingStates = permissionStatesMap.get(uuid) || []
              permissionStatesMap.set(uuid, [
                ...new Set([...existingStates, ...statesArray]),
              ])
            })
          })

          // Отфильтровать только те permissions, которые есть у пользователя
          const myPermissions = allPermissionsList.filter(
            (perm: any) => permissionUuids.has(perm.uuid),
          )

          // Преобразуем permissions в формат [{uuid, name, parentUuid, states}]
          const expandedPermissions: any[] = []

          myPermissions.forEach((perm: any) => {
            const states = permissionStatesMap.get(perm.uuid) || []

            if (states.length === 0) {
              return
            }

            expandedPermissions.push({
              uuid: perm.uuid,
              name: perm.name,
              parentUuid: perm.parentUuid,
              states: states,
            })

            states.forEach((state: string) => {
              const statePermissionName = `${perm.name}|${state}`
              expandedPermissions.push({
                uuid: perm.uuid,
                name: statePermissionName,
                parentUuid: perm.parentUuid,
                states: [state],
              })
            })
          })

          userData.permissions = expandedPermissions
        }
      } catch (err) {
        console.warn('[auth] _loadPermissions failed, falling back to empty permissions:', err)
        userData.permissions = []
      }
    },
    // Приводит сырой ответ MeQuery к структуре User с гарантированными дефолтами.
    // Нужно, т.к. batchData.MeQuery может быть undefined (partial-response), а даже
    // при успешном ответе отдельные поля (roleUuids/attributes) могут отсутствовать
    // из-за field-level access denied. Без нормализации в дальнейшем падает
    // authStore.user.roleUuids.some(...) в useL2Identity и подобных местах.
    _normalizeMe(raw: any) {
      const src = raw ?? {}
      return {
        ...defaultUser(),
        ...src,
        roleUuids: Array.isArray(src.roleUuids) ? src.roleUuids : [],
        roles: Array.isArray(src.roles) ? src.roles : [],
        permissions: Array.isArray(src.permissions) ? src.permissions : [],
      }
    },
    // Гидрация из батч-запроса: принимает сырые данные MeQuery и загружает permissions
    async hydrateMeAndPermissions(meData: any) {
      const normalized = this._normalizeMe(meData)

      await this._loadPermissions(normalized)

      this.user = devPemissions(normalized, [
        'root',
        'views|read',
        'views|edit',
        'views|create',
        'roles|read',
        'roles|edit',
        'roles|create',
        'entities|read',
        'entities|create',
        'attributes|read',
        'attributes|edit',
        'attributes|create',
      ])
      this.permissionsLoaded = true
    },
    async queryMe() {
      const res = await Srv.request(
        'query',
        'MeQuery',
        ['uuid', 'login', 'roleUuids', 'publicKey', 'attributes'],
        false,
      )

      const normalized = this._normalizeMe(res.data)

      await this._loadPermissions(normalized)

      this.user = devPemissions(normalized, [
        'root',
        'views|read',
        'views|edit',
        'views|create',
        'roles|read',
        'roles|edit',
        'roles|create',
        'entities|read',
        'entities|create',
        'attributes|read',
        'attributes|edit',
        'attributes|create',
      ])
      this.permissionsLoaded = true
    },
    checkPermission(column: { permission?: string }) {
      // Если permission не указан - разрешаем доступ
      if (!column.permission) {
        return true
      }

      // Проверяем наличие permission у пользователя
      const hasPermission = this.userPermissions.has(column.permission)

      // Отладка: выводим информацию о проверке
      console.log('🔐 Permission check:', {
        permission: column.permission,
        hasPermission,
        userPermissions: Array.from(this.userPermissions).filter((p) =>
          p.startsWith('address.'),
        ),
      })

      return hasPermission
    },
    async logout() {
      this.user = defaultUser()
      this.accessToken = null
      this.permissionsLoaded = false

      await router.push('/login')

      await Srv.request(
        'mutation',
        'LogoutMutation',
        ['result', 'message'],
        false,
      )

      accessTokenStorage.remove()
    },
    authWithAitu() {
      return guestSrv.requestQuery('mutation', 'aituStates')
    },
    async authAituAuthorizationCode(code: string) {
      const {data, type, message} = await guestSrv.requestQuery(
        'mutation',
        'authorizationCode',
        {code: code},
      )

      if (data?.accessToken) {
        accessTokenStorage.set(data.accessToken)
        this.accessToken = data.accessToken
      }

      return {type, message}
    },
    async createIdpState(lang?: string) {
      const {data, type, message} = await guestSrv.request(
        'mutation',
        'createIdpState',
        ['authUrl', 'state'],
        false,
        {lang},
      )

      return {data, type, message}
    },
    async idpAuthorizationCode(code: string, state: string) {
      const {data, type, message} = await guestSrv.request(
        'mutation',
        'idpAuthorizationCode',
        ['accessToken', 'expiresAt', 'user'],
        false,
        {code, state},
      )

      if (data?.accessToken) {
        accessTokenStorage.set(data.accessToken)
        this.accessToken = data.accessToken
      }

      return {data, type, message}
    },
    async createDigitalIdState() {
      const {data, type, message} = await guestSrv.request(
        'mutation',
        'createDigitalIdState',
        [],
        false,
        {},
      )

      return {data, type, message}
    },
    async digitalIdAuthorizationCode(code: string, state: string) {
      const {data, type, message} = await guestSrv.request(
        'mutation',
        'digitalIdAuthorizationCode',
        [],
        false,
        {code, state},
      )

      if (data?.accessToken) {
        accessTokenStorage.set(data.accessToken)
        this.accessToken = data.accessToken
      }

      return {data, type, message}
    },
  },
})
